import * as THREE from 'three';
import type { Tunables } from '../sim/tunables';

/** Behind-the-play camera (GDD 6.3). Always attacking up the screen (toward -z). */
export function createCamera(t: Tunables, aspect: number): THREE.PerspectiveCamera {
  const c = t.camera;
  return new THREE.PerspectiveCamera(c.fovDeg, aspect, c.near, c.far);
}

/**
 * call: low behind the QB, the hero shot over the play cards (reference 01).
 * qb: high behind the pocket, reading the field. chase: low behind the ball carrier.
 * ball: pulled back and up for passes in the air. celebrate: in front of the scorer looking back at him.
 * tackle: low and to the side of the pile, under the result banner (reference 07).
 */
export type CameraMode = 'call' | 'qb' | 'chase' | 'ball' | 'celebrate' | 'tackle';

/**
 * Smoothly follows a focus point. Shake decays on its own. A lens shift (fraction of screen height,
 * positive moves the picture up) keeps the subject clear of the cards and banners.
 */
export class CameraRig {
  private mode: CameraMode = 'qb';
  private readonly focus = new THREE.Vector3();
  private readonly pos = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private readonly wantPos = new THREE.Vector3();
  private readonly wantLook = new THREE.Vector3();
  private shake = 0;
  private jolt = 0;
  private time = 0;
  private shift = 0;
  private wantShift = 0;
  /** -1 attacks toward -z (offense). +1 for an interception return. */
  private dir = -1;
  private extraBack = 0;

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly t: Tunables,
  ) {}

  setMode(mode: CameraMode, dir: 1 | -1 = -1): void {
    this.mode = mode;
    this.dir = dir;
  }

  /** Extra pull back for long throws (yards). */
  setPullback(yards: number): void {
    this.extraBack = yards;
  }

  /** Jumps straight to the focus (between plays). */
  snapTo(x: number, z: number): void {
    this.follow(x, z);
    this.compute();
    this.pos.copy(this.wantPos);
    this.look.copy(this.wantLook);
    this.shift = this.wantShift;
    this.apply();
  }

  follow(x: number, z: number): void {
    // The focus stays on the field, so the camera never ends up staring into the stands.
    const half = this.t.field.lengthYards / 2;
    this.focus.set(x, 0, Math.max(-half, Math.min(half, z)));
  }

  /** Rattle that decays on its own. With jolt the camera also dips on the hit and springs back. */
  addShake(amount: number, jolt = false): void {
    this.shake = Math.min(1, this.shake + amount);
    if (jolt) this.jolt = 1;
  }

  update(dt: number): void {
    this.time += dt;
    this.compute();
    const t = this.t;
    const rate =
      this.mode === 'qb'
        ? t.cameraFollow.lerpPerSecond
        : this.mode === 'call'
          ? t.cameraCall.lerpPerSecond
          : this.mode === 'celebrate'
            ? t.cameraCelebrate.lerpPerSecond
            : this.mode === 'tackle'
              ? t.cameraTackle.lerpPerSecond
              : t.cameraChase.lerpPerSecond;
    const k = 1 - Math.exp(-rate * dt);
    this.pos.lerp(this.wantPos, k);
    this.look.lerp(this.wantLook, k);
    this.shift += (this.wantShift - this.shift) * k;
    this.shake = Math.max(0, this.shake - t.fx.shakeDecayPerSecond * dt * this.shake - dt * 0.2);
    this.jolt *= Math.exp(-t.fx.joltDecayPerSecond * dt);
    this.apply();
  }

  private compute(): void {
    const t = this.t;
    const c = t.camera;
    const ch = t.cameraChase;
    const f = this.focus;
    const d = this.dir;
    if (this.mode === 'call') {
      const cc = t.cameraCall;
      this.wantPos.set(0, cc.upYards, f.z - d * cc.backYards);
      this.wantLook.set(0, cc.lookHeightYards, f.z + d * cc.lookAheadYards);
      this.wantShift = cc.shift;
    } else if (this.mode === 'qb') {
      this.wantPos.set(f.x * 0.6, c.upYards, f.z - d * c.backYards);
      this.wantLook.set(f.x * 0.6, c.lookHeightYards, f.z + d * c.lookAheadYards);
      this.wantShift = c.shift;
    } else if (this.mode === 'chase') {
      this.wantPos.set(f.x, ch.upYards, f.z - d * ch.backYards);
      this.wantLook.set(f.x, ch.lookHeightYards, f.z + d * ch.lookAheadYards);
      this.wantShift = ch.shift;
    } else if (this.mode === 'ball') {
      this.wantPos.set(f.x * 0.7, ch.ballUpYards + this.extraBack * 0.35, f.z - d * (ch.ballBackYards + this.extraBack));
      this.wantLook.set(f.x, ch.lookHeightYards, f.z + d * ch.lookAheadYards);
      this.wantShift = ch.shift;
    } else if (this.mode === 'celebrate') {
      const cc = t.cameraCelebrate;
      const side = f.x > 0 ? -1 : 1;
      this.wantPos.set(f.x + side * cc.sideYards, cc.upYards, f.z + d * cc.frontYards);
      this.wantLook.set(f.x, cc.lookHeightYards, f.z);
      this.wantShift = cc.shift;
    } else {
      const ct = t.cameraTackle;
      const side = f.x > 0 ? -1 : 1;
      this.wantPos.set(f.x + side * ct.sideYards, ct.upYards, f.z - d * ct.backYards);
      this.wantLook.set(f.x, ct.lookHeightYards, f.z);
      this.wantShift = ct.shift;
    }
    // Stay inside the stadium bowl: never behind the end stands.
    const limit = t.field.lengthYards / 2 + c.insideFieldYards;
    this.wantPos.z = Math.max(-limit, Math.min(limit, this.wantPos.z));
  }

  private apply(): void {
    const fx = this.t.fx;
    const s = this.shake * fx.shakeYards;
    const ox = Math.sin(this.time * 47) * s;
    const oy = Math.cos(this.time * 53) * s * 0.7 - this.jolt * fx.joltYards;
    this.camera.position.set(this.pos.x + ox, this.pos.y + oy, this.pos.z);
    this.camera.lookAt(this.look.x + ox * 0.5, this.look.y + oy * 0.5, this.look.z);
    // setViewOffset also sets the aspect from the full size, so pass the current one through.
    const aspect = this.camera.aspect;
    if (Math.abs(this.shift) > 1e-4) this.camera.setViewOffset(aspect, 1, 0, this.shift, aspect, 1);
    else if (this.camera.view?.enabled) this.camera.clearViewOffset();
  }
}
